/** Profile vehicle / car details (optional, max 3, one primary). */

export const PROFILE_VEHICLES_MAX = 3;

/** Israeli license plates: digits only (normalized), typically 5–8 digits. */
export const ISRAEL_LICENSE_NUMBER_PATTERN = /^\d{5,8}$/;

export type ProfileVehicle = {
  /** Stable id from API, or client-generated when only local. */
  id: string;
  manufacturer: string;
  model: string;
  color: string;
  licenseNumber: string;
  isPrimary: boolean;
};

export type ProfileVehicleWire = {
  manufacturer: string;
  model: string;
  color: string;
  license_number: string;
  is_primary: boolean;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function pickString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function pickBoolean(...values: unknown[]): boolean | null {
  for (const value of values) {
    if (typeof value === 'boolean') {
      return value;
    }
    if (value === 'true' || value === 1 || value === '1') {
      return true;
    }
    if (value === 'false' || value === 0 || value === '0') {
      return false;
    }
  }
  return null;
}

export function normalizeIsraelLicenseNumber(raw: string): string {
  return raw.replace(/\D/g, '');
}

export function formatIsraelLicenseInput(raw: string): string {
  return normalizeIsraelLicenseNumber(raw).slice(0, 8);
}

export function isValidIsraelLicenseNumber(raw: string): boolean {
  return ISRAEL_LICENSE_NUMBER_PATTERN.test(normalizeIsraelLicenseNumber(raw));
}

export function createEmptyProfileVehicle(isPrimary = false): ProfileVehicle {
  return {
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    manufacturer: '',
    model: '',
    color: '',
    licenseNumber: '',
    isPrimary,
  };
}

/** Ensure at most one primary; promote first when any cars exist and none marked. */
export function normalizeProfileVehiclesPrimary(vehicles: ProfileVehicle[]): ProfileVehicle[] {
  if (vehicles.length === 0) {
    return [];
  }
  const primaryIndex = vehicles.findIndex((car) => car.isPrimary);
  const keepPrimary = primaryIndex >= 0 ? primaryIndex : 0;
  return vehicles.map((car, index) => ({
    ...car,
    isPrimary: index === keepPrimary,
  }));
}

export function setProfileVehiclePrimary(
  vehicles: ProfileVehicle[],
  vehicleId: string,
): ProfileVehicle[] {
  return vehicles.map((car) => ({
    ...car,
    isPrimary: car.id === vehicleId,
  }));
}

export function profileVehicleFromUnknown(raw: unknown, index: number): ProfileVehicle | null {
  if (!isPlainObject(raw)) {
    return null;
  }
  const manufacturer = pickString(raw.manufacturer, raw.make, raw.brand);
  const model = pickString(raw.model, raw.car_model, raw.carModel);
  const color = pickString(raw.color, raw.colour);
  const licenseRaw = pickString(
    raw.license_number,
    raw.licenseNumber,
    raw.plate_number,
    raw.plateNumber,
    raw.license_plate,
    raw.licensePlate,
  );
  if (!manufacturer && !model && !color && !licenseRaw) {
    return null;
  }
  const idRaw = pickString(raw.id, raw.vehicle_id, raw.vehicleId);
  const id =
    idRaw ||
    (typeof raw.id === 'number' && Number.isFinite(raw.id) ? String(raw.id) : null) ||
    `local-${index}`;
  const licenseNumber = licenseRaw ? normalizeIsraelLicenseNumber(licenseRaw) : '';
  return {
    id,
    manufacturer: manufacturer ?? '',
    model: model ?? '',
    color: color ?? '',
    licenseNumber,
    isPrimary: pickBoolean(raw.is_primary, raw.isPrimary, raw.primary) === true,
  };
}

export function extractProfileVehicles(record: Record<string, unknown> | null): ProfileVehicle[] {
  return readProfileVehiclesField(record)?.vehicles ?? [];
}

/** True when GET /profile included a vehicles (or cars) array — including empty. */
export function profileRecordIncludesVehiclesField(
  record: Record<string, unknown> | null,
): boolean {
  return readProfileVehiclesField(record) !== null;
}

function readProfileVehiclesField(
  record: Record<string, unknown> | null,
): { vehicles: ProfileVehicle[] } | null {
  if (!record) {
    return null;
  }
  const rawList =
    record.vehicles ??
    record.cars ??
    record.car_details ??
    record.carDetails ??
    (isPlainObject(record.private_client)
      ? record.private_client.vehicles ?? record.private_client.cars
      : undefined);
  if (rawList === undefined) {
    return null;
  }
  if (!Array.isArray(rawList)) {
    return { vehicles: [] };
  }
  const parsed = rawList
    .map((item, index) => profileVehicleFromUnknown(item, index))
    .filter((item): item is ProfileVehicle => item !== null)
    .slice(0, PROFILE_VEHICLES_MAX);
  return { vehicles: normalizeProfileVehiclesPrimary(parsed) };
}

/** PATCH /profile expects vehicles without client/server `id` (forbidden by validation). */
export function profileVehiclesToWire(vehicles: ProfileVehicle[]): ProfileVehicleWire[] {
  return normalizeProfileVehiclesPrimary(vehicles).map((car) => ({
    manufacturer: car.manufacturer.trim(),
    model: car.model.trim(),
    color: car.color.trim(),
    license_number: normalizeIsraelLicenseNumber(car.licenseNumber),
    is_primary: Boolean(car.isPrimary),
  }));
}

export type ProfileVehiclesValidationIssue =
  | 'max'
  | 'incomplete'
  | 'license'
  | 'duplicateLicense'
  | 'primary';

export function validateProfileVehicles(
  vehicles: ProfileVehicle[],
): ProfileVehiclesValidationIssue | null {
  if (vehicles.length > PROFILE_VEHICLES_MAX) {
    return 'max';
  }
  if (vehicles.length === 0) {
    return null;
  }
  const licenses = new Set<string>();
  let primaryCount = 0;
  for (const car of vehicles) {
    if (
      !car.manufacturer.trim() ||
      !car.model.trim() ||
      !car.color.trim() ||
      !car.licenseNumber.trim()
    ) {
      return 'incomplete';
    }
    const license = normalizeIsraelLicenseNumber(car.licenseNumber);
    if (!isValidIsraelLicenseNumber(license)) {
      return 'license';
    }
    if (licenses.has(license)) {
      return 'duplicateLicense';
    }
    licenses.add(license);
    if (car.isPrimary) {
      primaryCount += 1;
    }
  }
  if (primaryCount !== 1) {
    return 'primary';
  }
  return null;
}

/**
 * Prefer API vehicles when the profile payload includes `vehicles` (even if empty).
 * Local storage is only a legacy fallback when the API omits the field entirely.
 */
export function mergeProfileVehicles(
  apiVehicles: ProfileVehicle[],
  storedVehicles: ProfileVehicle[],
  apiIncludesVehiclesField: boolean,
): ProfileVehicle[] {
  if (apiIncludesVehiclesField) {
    return normalizeProfileVehiclesPrimary(apiVehicles.slice(0, PROFILE_VEHICLES_MAX));
  }
  return normalizeProfileVehiclesPrimary(storedVehicles.slice(0, PROFILE_VEHICLES_MAX));
}

export function formatProfileVehicleSummaryLine(car: ProfileVehicle): string {
  const parts = [car.manufacturer, car.model, car.color].map((part) => part.trim()).filter(Boolean);
  const plate = normalizeIsraelLicenseNumber(car.licenseNumber);
  if (plate) {
    parts.push(plate);
  }
  return parts.join(' · ');
}

/** PATCH /profile accepts a full `vehicles` replace (max 3). */
export const PROFILE_VEHICLES_PATCH_SUPPORTED = true;
